/* The ONAM introduction (18 Oct): once per visit, on the way in.

   The lockup builds in the middle of the page: the Azure tile first, the eight ink tiles following it round the grid
   the way the loader steps, then the word ONAM drawn left to right and BY SAPTHARUSHI settling under it. Then the
   words dissolve and the tiles glide across to the grid mark beside the Discover, Engage and Measure cards, while
   the rest of the page fades in already in place. The tiles land exactly on the page's own mark, which takes over.

   /home (MAP deciding where to send you) shows the build; /entry-hub finishes it. The two share a start time
   (sessionStorage), so if the landing is quick the hub picks the build up where it was, and if it is slow the hub
   goes straight to the move. A click or a key skips it. Reduced motion, or a second visit in the same session,
   shows none of it. Only transform and opacity move during the flight. */

import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { LOCKUP } from "../lockup";
import { reducedMotion } from "../mark";

const T0 = "onam-intro-t0";
const DONE = "onam-intro-done";
const BUILD = 2.0; // seconds until the lockup is complete and may move

const store = {
  get: (k: string) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
};

/** Whether the introduction should still play in this visit. */
export const introPending = () => !reducedMotion() && store.get(DONE) !== "1";

/** Seconds since the introduction started in this visit (starting it now if it hasn't). */
function elapsed() {
  let t = Number(store.get(T0));
  if (!t || Date.now() - t > 60000) { t = Date.now(); store.set(T0, String(t)); }
  return (Date.now() - t) / 1000;
}

const lockupMarkup = () => {
  try {
    const svg = atob(LOCKUP.split("base64,")[1] || "");
    return svg.replace(/\swidth="[^"]*"/, "").replace(/\sheight="[^"]*"/, "").replace(/<title>[^<]*<\/title>/, "");
  } catch { return ""; }
};

export default function OnamIntro({ mode, onReveal, onDone }: { mode: "landing" | "hub"; onReveal?: () => void; onDone?: () => void }) {
  const root = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const markup = useMemo(lockupMarkup, []);
  const t = useMemo(elapsed, []);
  const ended = useRef(false);

  useEffect(() => {
    if (mode !== "hub") return;
    const el = root.current, st = stage.current;
    if (!el || !st) return;
    const anims: Animation[] = [];
    const finish = () => {
      if (ended.current) return;
      ended.current = true;
      store.set(DONE, "1");
      onDone && onDone();
    };
    const fly = (fast = false) => {
      if (el.dataset.flying) return;
      el.dataset.flying = "1";
      // the tiles' box in the lockup (viewBox 7 7 179.84 66.55: tiles span 7..57) onto the tiles' box in the hub's
      // own mark (Loader64, viewBox 0 0 64 64: tiles span 7..57)
      const target = document.querySelector(".hubp .hub-mark .onam-ld") as HTMLElement | null;
      const from = st.getBoundingClientRect();
      onReveal && onReveal();
      const dur = fast ? 300 : 900;
      const words = Array.from(st.querySelectorAll("svg > path")) as SVGElement[];
      words.forEach((w) => anims.push(w.animate([{ opacity: 1, filter: "blur(0px)" }, { opacity: 0, filter: "blur(3px)" }],
        { duration: fast ? 150 : 420, easing: "ease-out", fill: "forwards" })));
      if (target && from.width) {
        const to = target.getBoundingClientRect();
        const fromTiles = { left: from.left, top: from.top, size: from.width * (50 / 179.84) };
        const toTiles = { left: to.left + to.width * (7 / 64), top: to.top + to.height * (7 / 64), size: to.width * (50 / 64) };
        const s = toTiles.size / fromTiles.size;
        const dx = toTiles.left - fromTiles.left, dy = toTiles.top - fromTiles.top;
        anims.push(st.animate([{ transform: "none" }, { transform: `translate(${dx}px, ${dy}px) scale(${s})` }],
          { duration: dur, delay: fast ? 0 : 160, easing: "cubic-bezier(.65,0,.25,1)", fill: "forwards" }));
      } else {
        anims.push(st.animate([{ opacity: 1 }, { opacity: 0 }], { duration: dur, fill: "forwards" }));
      }
      const bg = el.querySelector(".oi-bg") as HTMLElement | null;
      if (bg) anims.push(bg.animate([{ opacity: 1 }, { opacity: 0 }], { duration: fast ? 220 : 620, delay: fast ? 0 : 140, easing: "ease", fill: "forwards" }));
      const label = el.querySelector(".oi-label") as HTMLElement | null;
      if (label) anims.push(label.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, fill: "forwards" }));
      window.setTimeout(finish, dur + (fast ? 20 : 200));
    };
    const wait = Math.max(0, (BUILD - t) * 1000) + (t > BUILD ? 220 : 0);
    const timer = window.setTimeout(() => fly(false), wait);
    const skip = () => { window.clearTimeout(timer); fly(true); };
    window.addEventListener("keydown", skip, { once: true });
    el.addEventListener("pointerdown", skip, { once: true });
    return () => { window.clearTimeout(timer); window.removeEventListener("keydown", skip); anims.forEach((a) => a.cancel()); };
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // on top of everything, header included, until it hands over
  return createPortal(
    <div className={`onam-intro ${mode}`} ref={root} style={{ ["--t0" as any]: `${t.toFixed(3)}s` }} aria-hidden="true">
      <div className="oi-bg" />
      <div className="oi-center">
        <div className="oi-stage" ref={stage} dangerouslySetInnerHTML={{ __html: markup }} />
        {mode === "landing" ? <span className="label oi-label">Getting things ready</span> : null}
      </div>
    </div>,
    document.body,
  );
}
